import { Capacitor } from '@capacitor/core';
import { encodeAppleState, type AppleAuthState } from '@alterrego/core-config';

export interface AppleSignInOptions {
  serviceId: string;
  redirectUri: string;
  /**
   * Contexte à faire suivre à Apple pour le flux web (redirect). Requis sur le
   * web : c'est lui qui ramène l'utilisateur sur la bonne origine, avec le bon
   * tenant/env. Ignoré en natif (le SDK iOS rend le résultat en direct).
   */
  state?: AppleAuthState;
}

export interface AppleSignInResult {
  authorizationCode: string;
  identityToken: string;
  email: string | null;
  fullName: { givenName: string | null; familyName: string | null } | null;
}

/**
 * Échec du Sign in with Apple, porteur du code d'erreur d'origine.
 *
 * `code` reprend tel quel le code Apple (`invalid_client`,
 * `popup_closed_by_user`…) quand il est disponible, sinon `unknown`. C'est lui
 * qu'il faut tester — jamais le texte du message, qui n'est pas un contrat.
 */
export class AppleSignInError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'AppleSignInError';
    this.code = code;
  }
}

/**
 * Codes Apple signifiant « l'utilisateur a renoncé » : ce n'est pas une panne,
 * l'appelant ne doit pas alerter dessus.
 */
const CANCEL_CODES = new Set([
  'popup_closed_by_user',
  'user_cancelled_authorize',
  'user_trigger_new_signin_flow',
]);

/** `true` si l'échec est un abandon utilisateur et non une erreur à signaler. */
export function isAppleSignInCancel(err: unknown): boolean {
  return err instanceof AppleSignInError && CANCEL_CODES.has(err.code);
}

/**
 * Normalise ce que rejette Apple en `AppleSignInError`.
 *
 * Le SDK JS d'Apple rejette avec un objet nu — `{ error: 'invalid_client' }` —
 * et non une `Error` : `err.message` vaut `undefined` et `String(err)` donne
 * « [object Object] ». Sans cette normalisation, le code réel (la seule donnée
 * exploitable) est perdu avant d'atteindre l'appelant.
 */
export function toAppleSignInError(err: unknown): AppleSignInError {
  if (err instanceof AppleSignInError) return err;

  const rawCode = (err as { error?: unknown } | null)?.error;
  if (typeof rawCode === 'string' && rawCode) {
    return new AppleSignInError(rawCode, `Apple Sign In: ${rawCode}`);
  }

  if (err instanceof Error) return new AppleSignInError('unknown', err.message);

  return new AppleSignInError('unknown', `Apple Sign In: ${describe(err)}`);
}

/** Rend un rejet non-Error lisible dans une alerte, sans jamais throw. */
function describe(err: unknown): string {
  try {
    const json = JSON.stringify(err);
    if (json && json !== '{}') return json;
  } catch {
    // objet circulaire → on retombe sur String()
  }
  return String(err);
}

/**
 * Sign in with Apple — dual path:
 * - Native (iOS): uses ASAuthorizationController via Capacitor plugin
 * - Web: uses Apple JS SDK popup/redirect flow
 *
 * Rejette toujours une `AppleSignInError` : voir `toAppleSignInError`.
 */
export async function signInWithApple(options: AppleSignInOptions): Promise<AppleSignInResult> {
  try {
    if (Capacitor.isNativePlatform()) {
      return await signInNative(options);
    }
    return await signInWeb(options);
  } catch (err) {
    throw toAppleSignInError(err);
  }
}

/**
 * Native iOS flow via @nicepromise/capacitor-apple-sign-in
 * (or similar Capacitor plugin — will be configured in the app's capacitor.config.ts)
 */
async function signInNative(options: AppleSignInOptions): Promise<AppleSignInResult> {
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');

  const result = await SignInWithApple.authorize({
    clientId: options.serviceId,
    redirectURI: options.redirectUri,
    scopes: 'email name',
  });

  const response = result.response;
  if (!response.authorizationCode || !response.identityToken) {
    throw new AppleSignInError('user_cancelled_authorize', 'Apple Sign In cancelled or failed');
  }

  return {
    authorizationCode: response.authorizationCode,
    identityToken: response.identityToken,
    email: response.email ?? null,
    fullName: response.givenName || response.familyName
      ? { givenName: response.givenName ?? null, familyName: response.familyName ?? null }
      : null,
  };
}

/** URL d'autorisation Apple — construite ici, cf. `signInWeb`. */
export function buildAppleAuthorizeUrl(options: AppleSignInOptions): string {
  const params = new URLSearchParams({
    client_id: options.serviceId,
    redirect_uri: options.redirectUri,
    // `id_token` en plus de `code` : le callback backend vérifie l'id_token pour
    // établir l'identité. Avec `code` seul, Apple ne POSTe pas d'id_token.
    response_type: 'code id_token',
    scope: 'email name',
    // Imposé par Apple dès que le scope contient email/name — et c'est bien un
    // POST qu'attend `/auth/apple/callback`.
    response_mode: 'form_post',
  });
  if (options.state) params.set('state', encodeAppleState(options.state));
  return `https://appleid.apple.com/auth/authorize?${params.toString()}`;
}

/**
 * Flux web : redirection pleine page (PAS de popup).
 *
 * Le mode popup du SDK JS d'Apple est inutilisable ici. Il rend le résultat par
 * `postMessage` en ciblant l'origine du `redirectURI` — notre backend
 * (`api.apps.alterrego.com`) — alors que la page appelante est servie depuis le
 * domaine du tenant (`nodo.apps.alterrego.com`). Le navigateur refuse :
 *
 *   Unable to post message to https://api.apps.alterrego.com.
 *   Recipient has origin https://nodo.apps.alterrego.com
 *
 * La popup se ferme sans résultat, et Apple rejette `popup_closed_by_user` —
 * indiscernable d'un abandon utilisateur. Le mode popup exigerait un
 * `redirectURI` de MÊME origine que la page, donc une Return URL Apple par
 * domaine de tenant : ça ne passe pas à l'échelle et c'est une action portail à
 * chaque onboarding. La redirection pleine page n'a pas de contrainte
 * d'origine : Apple POSTe côté serveur sur la Return URL déjà enregistrée, et
 * le backend nous ramène sur le bon domaine via `state`.
 *
 * Ne s'applique qu'au web : le natif passe par le SDK iOS, sans postMessage —
 * c'est pourquoi Sauf Imprévu natif n'a jamais vu ce bug.
 *
 * Ne résout jamais : la page navigue. Le retour se fait sur `returnUrl`, avec
 * le JWT en fragment.
 */
function signInWeb(options: AppleSignInOptions): Promise<AppleSignInResult> {
  window.location.assign(buildAppleAuthorizeUrl(options));
  // La navigation est asynchrone : sans ça, l'appelant verrait un rejet et
  // alerterait juste avant que la page ne parte.
  return new Promise<AppleSignInResult>(() => {});
}

